// MC487A — development-only Live Count traffic probe v2.
//
// This utility NEVER reads/writes Firestore. It only observes payloads that the
// existing Morpheus cloud layer is already sending/receiving and keeps lightweight
// counters in this browser. The probe is enabled on localhost/development builds,
// or explicitly with localStorage: mc:dev-traffic-probe = "1".
//
// v2 adds:
// - application write attempts (attempt 1 / 2 / 3)
// - acknowledgement mismatches / acknowledgement read failures
// - transaction/write failures
// - listener metadata split (server / cache / pending local write)
// - raw `value` bytes vs duplicated `json` bytes
// - estimated full Firestore document field payload
// - per-key breakdown for Count Ledger / session list / related session documents

const RUNTIME_KEY = "__mcTrafficProbeRuntime";
const ENABLE_FLAG_KEY = "mc:dev-traffic-probe";
let cachedEnabled = null;

function browserWindow() {
  return typeof window !== "undefined" ? window : null;
}

export function isMcTrafficProbeEnabled() {
  if (cachedEnabled !== null) return cachedEnabled;
  const win = browserWindow();
  if (!win) return false;
  const host = String(win.location?.hostname || "").toLowerCase();
  const isLocalHost = host === "localhost" || host === "127.0.0.1" || host === "::1";
  let explicit = false;
  try { explicit = win.localStorage?.getItem(ENABLE_FLAG_KEY) === "1"; } catch (_) {}
  let devBuild = false;
  try {
    const nodeEnv = typeof process !== "undefined" ? process?.env?.NODE_ENV : "";
    devBuild = Boolean(nodeEnv && nodeEnv !== "production");
  } catch (_) {}
  cachedEnabled = Boolean(isLocalHost || explicit || devBuild);
  return cachedEnabled;
}

function emptyKeyStats(key = "") {
  return {
    key: String(key || ""),
    logicalSaves: 0,
    logicalSaveBytes: 0,
    writeAttempts: 0,
    attempt1: 0,
    attempt2: 0,
    attempt3: 0,
    firestoreWrites: 0,
    firestoreWriteBytes: 0,
    duplicatedJsonWriteBytes: 0,
    estimatedDocumentWriteBytes: 0,
    firestoreReads: 0,
    firestoreReadBytes: 0,
    estimatedDocumentReadBytes: 0,
    directReads: 0,
    ackReads: 0,
    transactionReads: 0,
    directEstimatedDocumentBytes: 0,
    ackEstimatedDocumentBytes: 0,
    transactionEstimatedDocumentBytes: 0,
    listenerSnapshots: 0,
    listenerBytes: 0,
    listenerServerSnapshots: 0,
    listenerServerBytes: 0,
    listenerCacheSnapshots: 0,
    listenerCacheBytes: 0,
    listenerPendingSnapshots: 0,
    listenerPendingBytes: 0,
    ackMismatches: 0,
    ackReadFailures: 0,
    transactionFailures: 0,
    failedWrites: 0,
    firestoreDeletes: 0,
    callableCalls: 0,
    callableRequestBytes: 0,
    callableResponseBytes: 0,
    largestPayloadBytes: 0,
    largestEstimatedDocumentBytes: 0,
  };
}

function emptyStats(sessionId = "") {
  return {
    sessionId: String(sessionId || ""),
    startedAt: new Date().toISOString(),
    edits: 0,
    logicalSaves: 0,
    logicalSaveBytes: 0,
    writeAttempts: 0,
    attempt1: 0,
    attempt2: 0,
    attempt3: 0,
    firestoreWrites: 0,
    firestoreWriteBytes: 0,
    duplicatedJsonWriteBytes: 0,
    estimatedDocumentWriteBytes: 0,
    firestoreReads: 0,
    firestoreReadBytes: 0,
    estimatedDocumentReadBytes: 0,
    directReads: 0,
    ackReads: 0,
    transactionReads: 0,
    directEstimatedDocumentBytes: 0,
    ackEstimatedDocumentBytes: 0,
    transactionEstimatedDocumentBytes: 0,
    listenerSnapshots: 0,
    listenerBytes: 0,
    listenerServerSnapshots: 0,
    listenerServerBytes: 0,
    listenerCacheSnapshots: 0,
    listenerCacheBytes: 0,
    listenerPendingSnapshots: 0,
    listenerPendingBytes: 0,
    ackMismatches: 0,
    ackReadFailures: 0,
    transactionFailures: 0,
    failedWrites: 0,
    firestoreDeletes: 0,
    callableCalls: 0,
    callableRequestBytes: 0,
    callableResponseBytes: 0,
    largestPayloadBytes: 0,
    largestEstimatedDocumentBytes: 0,
    byKey: {},
    lastEventAt: null,
  };
}

function runtime() {
  const win = browserWindow();
  if (!win) return { activeSessionId: "", activeWorkflowId: "", sessions: {} };
  if (!win[RUNTIME_KEY]) win[RUNTIME_KEY] = { activeSessionId: "", activeWorkflowId: "", sessions: {} };
  if (!("activeWorkflowId" in win[RUNTIME_KEY])) win[RUNTIME_KEY].activeWorkflowId = "";
  return win[RUNTIME_KEY];
}

function statsFor(sessionId, create = true) {
  const clean = String(sessionId || "").trim();
  if (!clean) return null;
  const rt = runtime();
  if (!rt.sessions[clean] && create) rt.sessions[clean] = emptyStats(clean);
  return rt.sessions[clean] || null;
}

function keyStatsFor(stats, key = "") {
  if (!stats) return null;
  const clean = String(key || "unknown");
  if (!stats.byKey || typeof stats.byKey !== "object") stats.byKey = {};
  if (!stats.byKey[clean]) stats.byKey[clean] = emptyKeyStats(clean);
  return stats.byKey[clean];
}

export function mcTrafficByteLength(value) {
  const raw = typeof value === "string" ? value : String(value ?? "");
  try {
    if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(raw).length;
  } catch (_) {}
  // Conservative UTF-16 fallback when TextEncoder is unavailable.
  return raw.length * 2;
}

function safeJsonByteLength(value) {
  if (value === undefined) return 0;
  try {
    return mcTrafficByteLength(JSON.stringify(value));
  } catch (_) {
    return 0;
  }
}


export function getMcTrafficProbeRouteContext(locationLike = null) {
  const win = browserWindow();
  const pathname = String(locationLike?.pathname ?? win?.location?.pathname ?? "").replace(/\/$/, "") || "/";
  const hash = String(locationLike?.hash ?? win?.location?.hash ?? "").toLowerCase();

  const liveMatch = pathname.match(/^\/morpheus-count\/session\/([^/]+)$/i);
  if (liveMatch) {
    const sessionId = decodeURIComponent(liveMatch[1] || "");
    return {
      probeId: sessionId,
      label: "Live Count",
      title: "Live Count Traffic",
      subtitle: "Measures the active count's existing Firestore path. No probe network calls are added.",
      routeKey: "live-count",
    };
  }

  const archiveDetail = pathname.match(/^\/morpheus-count\/archive(?:\/.*)?$/i);
  if (archiveDetail) return { probeId: "workflow:archives", label: "Archives", title: "Archives Traffic", subtitle: "Measures archive discovery, comparison, restore and finalized-count traffic on this browser.", routeKey: "archives" };

  if (pathname === "/morpheus-count/imports") return { probeId: "workflow:intake", label: "Intake", title: "Inventory Intake Traffic", subtitle: "Measures active-inventory pointers, manifests, chunks, storage authorities and import work already performed by Intake.", routeKey: "intake" };
  if (pathname === "/morpheus-count/prepared-data") return { probeId: "workflow:review", label: "Review / SOH", title: "Inventory Review Traffic", subtitle: "Measures Review and Stock on Hand hydration, decisions, storage authorities and preparation work already performed by this page.", routeKey: "review" };
  if (pathname === "/morpheus-count/sessions") return { probeId: "workflow:sessions", label: "Sessions", title: "Sessions Traffic", subtitle: "Measures session discovery, active-count hydration and live session authorities already used by Sessions.", routeKey: "sessions" };
  if (pathname === "/morpheus-count/sections") return { probeId: "workflow:sections", label: "Sections", title: "Sections Traffic", subtitle: "Measures the existing Firestore work used by Sections.", routeKey: "sections" };
  if (pathname === "/morpheus-count/sites") return { probeId: "workflow:sites", label: "Sites", title: "Sites Traffic", subtitle: "Measures the existing Firestore work used by Sites and site configuration.", routeKey: "sites" };
  if (pathname === "/morpheus-count/config") return { probeId: "workflow:config", label: "Config", title: "Config Traffic", subtitle: "Measures shared Core Config, Response Design and related configuration traffic.", routeKey: "config" };
  if (pathname === "/morpheus-count/mapping") return { probeId: "workflow:mapping", label: "Mapping", title: "Mapping Traffic", subtitle: "Measures the existing Firestore work used by Mapping.", routeKey: "mapping" };
  if (pathname === "/morpheus-count/categories") return { probeId: "workflow:inventory-config", label: "Inventory Config", title: "Inventory Configuration Traffic", subtitle: "Measures category, Count Group, Unit Intelligence and inventory-configuration traffic.", routeKey: "inventory-config" };
  if (pathname === "/morpheus-count/profiles") return { probeId: "workflow:people", label: "People", title: "People Traffic", subtitle: "Measures Morpheus profile and access-layer traffic used by People.", routeKey: "people" };
  if (pathname === "/morpheus-count/notifications" || pathname === "/morpheus-count/whatsapp-settings") return { probeId: "workflow:response-intelligence", label: "Response Intelligence", title: "Response Intelligence Traffic", subtitle: "Measures the existing response-routing and notification configuration traffic.", routeKey: "response-intelligence" };

  if (pathname === "/morpheus-count/workspace") {
    if (hash === "#responses") return { probeId: "workflow:responses", label: "Responses", title: "Responses Traffic", subtitle: "Measures the existing response and operational-action traffic.", routeKey: "responses" };
    if (hash === "#notifications") return { probeId: "workflow:response-intelligence", label: "Response Intelligence", title: "Response Intelligence Traffic", subtitle: "Measures the existing response-routing and notification traffic.", routeKey: "response-intelligence" };
    if (hash === "#tickets") return { probeId: "workflow:tickets", label: "My Tickets", title: "My Tickets Traffic", subtitle: "Measures Morpheus support-ticket traffic observed by the dev probe.", routeKey: "tickets" };
    if (hash === "#billing") return { probeId: "workflow:billing", label: "Billing", title: "Billing Traffic", subtitle: "Measures Morpheus billing and usage traffic observed by the dev probe.", routeKey: "billing" };
    return { probeId: "workflow:dashboard", label: "Dashboard", title: "Dashboard Traffic", subtitle: "Measures the existing Firestore work used to build the Morpheus dashboard.", routeKey: "dashboard" };
  }

  return { probeId: `workflow:${pathname || "morpheus"}${hash || ""}`, label: "Morpheus", title: "Morpheus Traffic", subtitle: "Measures this Morpheus route's existing Firestore path. No probe network calls are added.", routeKey: "morpheus" };
}

export function getCurrentMcTrafficProbeId() {
  return getMcTrafficProbeRouteContext().probeId;
}

function sessionForKey(key = "") {
  const rt = runtime();
  const active = String(rt.activeSessionId || "").trim();
  const cleanKey = String(key || "");
  if (active) {
    if (cleanKey === "mc-session-list") return active;
    if (cleanKey.includes(`:${active}`)) return active;
  }

  // Intake / Review use a page-scoped workflow probe. While one of those pages
  // is active we intentionally observe every Firestore operation routed through
  // the Morpheus cloud layer, regardless of key. This is diagnostic-only and
  // does not add any network calls.
  const workflow = String(rt.activeWorkflowId || "").trim();
  if (workflow) return workflow;

  // MC492 — global Morpheus dev inspection. Every route now has an automatic
  // page-scoped probe id, so ordinary mcCloudSync traffic is measurable without
  // each page mounting its own probe lifecycle. This reads only window.location;
  // it performs no network work and does not change the cloud path.
  return String(getMcTrafficProbeRouteContext().probeId || "").trim();
}

function touch(stats, bytes = 0, estimatedDocumentBytes = 0) {
  if (!stats) return;
  stats.lastEventAt = new Date().toISOString();
  stats.largestPayloadBytes = Math.max(Number(stats.largestPayloadBytes || 0), Number(bytes || 0));
  stats.largestEstimatedDocumentBytes = Math.max(
    Number(stats.largestEstimatedDocumentBytes || 0),
    Number(estimatedDocumentBytes || 0)
  );
}

function touchKey(stats, bytes = 0, estimatedDocumentBytes = 0) {
  if (!stats) return;
  stats.largestPayloadBytes = Math.max(Number(stats.largestPayloadBytes || 0), Number(bytes || 0));
  stats.largestEstimatedDocumentBytes = Math.max(
    Number(stats.largestEstimatedDocumentBytes || 0),
    Number(estimatedDocumentBytes || 0)
  );
}

function documentByteEstimate(documentData, fallbackRaw = "") {
  const measured = safeJsonByteLength(documentData);
  return measured > 0 ? measured : mcTrafficByteLength(fallbackRaw || "");
}

export function activateMcTrafficProbeSession(sessionId) {
  if (!isMcTrafficProbeEnabled()) return () => {};
  const clean = String(sessionId || "").trim();
  if (!clean) return () => {};
  const rt = runtime();
  rt.activeSessionId = clean;
  statsFor(clean, true);
  return () => {
    const current = runtime();
    if (current.activeSessionId === clean) current.activeSessionId = "";
  };
}


export function activateMcTrafficProbeWorkflow(workflowId) {
  if (!isMcTrafficProbeEnabled()) return () => {};
  const clean = String(workflowId || "").trim();
  if (!clean) return () => {};
  const rt = runtime();
  rt.activeWorkflowId = clean;
  statsFor(clean, true);
  return () => {
    const current = runtime();
    if (current.activeWorkflowId === clean) current.activeWorkflowId = "";
  };
}

export function recordMcTrafficEdit(sessionId) {
  if (!isMcTrafficProbeEnabled()) return;
  const stats = statsFor(sessionId, true);
  if (!stats) return;
  stats.edits += 1;
  touch(stats, 0);
}

export function recordMcTrafficLogicalSave(sessionId, raw, key = "") {
  if (!isMcTrafficProbeEnabled()) return;
  const stats = statsFor(sessionId, true);
  if (!stats) return;
  const bytes = mcTrafficByteLength(raw);
  stats.logicalSaves += 1;
  stats.logicalSaveBytes += bytes;
  if (key) {
    const perKey = keyStatsFor(stats, key);
    perKey.logicalSaves += 1;
    perKey.logicalSaveBytes += bytes;
    touchKey(perKey, bytes);
  }
  touch(stats, bytes);
}

export function recordMcTrafficWriteAttempt(key, attempt = 1) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  const n = Math.max(1, Math.min(3, Number(attempt || 1)));
  stats.writeAttempts += 1;
  perKey.writeAttempts += 1;
  if (n === 1) { stats.attempt1 += 1; perKey.attempt1 += 1; }
  else if (n === 2) { stats.attempt2 += 1; perKey.attempt2 += 1; }
  else { stats.attempt3 += 1; perKey.attempt3 += 1; }
  touch(stats, 0);
}

export function recordMcTrafficAckMismatch(key, attempt = 1) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  stats.ackMismatches += 1;
  perKey.ackMismatches += 1;
  touch(stats, 0);
}

export function recordMcTrafficFirestoreRead(key, raw, reason = "direct", options = {}) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  const bytes = mcTrafficByteLength(raw || "");
  const estimatedDocumentBytes = documentByteEstimate(options?.documentData, raw || "");

  stats.firestoreReads += 1;
  stats.firestoreReadBytes += bytes;
  stats.estimatedDocumentReadBytes += estimatedDocumentBytes;
  perKey.firestoreReads += 1;
  perKey.firestoreReadBytes += bytes;
  perKey.estimatedDocumentReadBytes += estimatedDocumentBytes;

  if (reason === "listener") {
    stats.listenerSnapshots += 1;
    stats.listenerBytes += bytes;
    perKey.listenerSnapshots += 1;
    perKey.listenerBytes += bytes;

    const fromCache = options?.fromCache === true;
    const hasPendingWrites = options?.hasPendingWrites === true;
    if (!fromCache && !hasPendingWrites) {
      stats.listenerServerSnapshots += 1;
      stats.listenerServerBytes += estimatedDocumentBytes;
      perKey.listenerServerSnapshots += 1;
      perKey.listenerServerBytes += estimatedDocumentBytes;
    }
    if (fromCache) {
      stats.listenerCacheSnapshots += 1;
      stats.listenerCacheBytes += estimatedDocumentBytes;
      perKey.listenerCacheSnapshots += 1;
      perKey.listenerCacheBytes += estimatedDocumentBytes;
    }
    if (hasPendingWrites) {
      stats.listenerPendingSnapshots += 1;
      stats.listenerPendingBytes += estimatedDocumentBytes;
      perKey.listenerPendingSnapshots += 1;
      perKey.listenerPendingBytes += estimatedDocumentBytes;
    }
  } else if (reason === "ack") {
    stats.ackReads += 1;
    stats.ackEstimatedDocumentBytes += estimatedDocumentBytes;
    perKey.ackReads += 1;
    perKey.ackEstimatedDocumentBytes += estimatedDocumentBytes;
  } else if (reason === "transaction") {
    stats.transactionReads += 1;
    stats.transactionEstimatedDocumentBytes += estimatedDocumentBytes;
    perKey.transactionReads += 1;
    perKey.transactionEstimatedDocumentBytes += estimatedDocumentBytes;
  } else {
    stats.directReads += 1;
    stats.directEstimatedDocumentBytes += estimatedDocumentBytes;
    perKey.directReads += 1;
    perKey.directEstimatedDocumentBytes += estimatedDocumentBytes;
  }
  touchKey(perKey, bytes, estimatedDocumentBytes);
  touch(stats, bytes, estimatedDocumentBytes);
}

export function recordMcTrafficFirestoreWrite(key, raw, options = {}) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  const bytes = mcTrafficByteLength(raw || "");
  const jsonBytes = options?.jsonValue === undefined || options?.jsonValue === null
    ? 0
    : safeJsonByteLength(options.jsonValue);
  const estimatedDocumentBytes = documentByteEstimate(options?.documentData, raw || "");

  stats.firestoreWrites += 1;
  stats.firestoreWriteBytes += bytes;
  stats.duplicatedJsonWriteBytes += jsonBytes;
  stats.estimatedDocumentWriteBytes += estimatedDocumentBytes;
  perKey.firestoreWrites += 1;
  perKey.firestoreWriteBytes += bytes;
  perKey.duplicatedJsonWriteBytes += jsonBytes;
  perKey.estimatedDocumentWriteBytes += estimatedDocumentBytes;

  if (options?.ok === false) {
    stats.failedWrites += 1;
    perKey.failedWrites += 1;
  }
  touchKey(perKey, bytes, estimatedDocumentBytes);
  touch(stats, bytes, estimatedDocumentBytes);
}

export function recordMcTrafficWriteFailure(key, options = {}) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  if (options?.phase === "ack-read") {
    stats.ackReadFailures += 1;
    perKey.ackReadFailures += 1;
  } else {
    stats.failedWrites += 1;
    perKey.failedWrites += 1;
    if (options?.phase === "transaction" || options?.phase === "write") {
      stats.transactionFailures += 1;
      perKey.transactionFailures += 1;
    }
  }
  touch(stats, 0);
}

export function recordMcTrafficFirestoreDelete(key) {
  if (!isMcTrafficProbeEnabled()) return;
  const sessionId = sessionForKey(key);
  if (!sessionId) return;
  const stats = statsFor(sessionId, true);
  const perKey = keyStatsFor(stats, key);
  stats.firestoreDeletes += 1;
  perKey.firestoreDeletes += 1;
  touch(stats, 0);
}

export function recordMcTrafficCallableRequest(probeId, key, payload) {
  if (!isMcTrafficProbeEnabled()) return;
  const stats = statsFor(probeId, true);
  if (!stats) return;
  const perKey = keyStatsFor(stats, key || "callable:unknown");
  const bytes = safeJsonByteLength(payload);
  stats.callableCalls += 1;
  stats.callableRequestBytes += bytes;
  perKey.callableCalls += 1;
  perKey.callableRequestBytes += bytes;
  touchKey(perKey, bytes);
  touch(stats, bytes);
}

export function recordMcTrafficCallableResponse(probeId, key, payload) {
  if (!isMcTrafficProbeEnabled()) return;
  const stats = statsFor(probeId, true);
  if (!stats) return;
  const perKey = keyStatsFor(stats, key || "callable:unknown");
  const bytes = safeJsonByteLength(payload);
  stats.callableResponseBytes += bytes;
  perKey.callableResponseBytes += bytes;
  touchKey(perKey, bytes);
  touch(stats, bytes);
}

export function getMcTrafficProbeSnapshot(sessionId) {
  const stats = statsFor(sessionId, false) || emptyStats(sessionId);
  const logicalSaves = Number(stats.logicalSaves || 0);
  const edits = Number(stats.edits || 0);
  const estimatedDocumentWriteBytes = Number(stats.estimatedDocumentWriteBytes || 0);
  const estimatedDocumentReadBytes = Number(stats.estimatedDocumentReadBytes || 0);
  const byKey = Object.fromEntries(
    Object.entries(stats.byKey || {}).map(([key, value]) => [key, { ...emptyKeyStats(key), ...(value || {}) }])
  );
  return {
    ...emptyStats(sessionId),
    ...stats,
    byKey,
    totalObservedBytes: Number(stats.firestoreWriteBytes || 0) + Number(stats.firestoreReadBytes || 0),
    totalEstimatedDocumentBytes: estimatedDocumentWriteBytes + estimatedDocumentReadBytes,
    serverPathEstimatedDocumentBytes:
      estimatedDocumentWriteBytes
      + Number(stats.transactionEstimatedDocumentBytes || 0)
      + Number(stats.ackEstimatedDocumentBytes || 0)
      + Number(stats.directEstimatedDocumentBytes || 0)
      + Number(stats.listenerServerBytes || 0),
    networkPathEstimatedBytes:
      estimatedDocumentWriteBytes
      + Number(stats.transactionEstimatedDocumentBytes || 0)
      + Number(stats.ackEstimatedDocumentBytes || 0)
      + Number(stats.directEstimatedDocumentBytes || 0)
      + Number(stats.listenerServerBytes || 0)
      + Number(stats.callableRequestBytes || 0)
      + Number(stats.callableResponseBytes || 0),
    coalescingRatio: logicalSaves > 0 ? edits / logicalSaves : 0,
    writeAmplification: logicalSaves > 0 ? Number(stats.firestoreWrites || 0) / logicalSaves : 0,
  };
}

export function resetMcTrafficProbe(sessionId) {
  if (!isMcTrafficProbeEnabled()) return getMcTrafficProbeSnapshot(sessionId);
  const clean = String(sessionId || "").trim();
  if (!clean) return getMcTrafficProbeSnapshot(clean);
  runtime().sessions[clean] = emptyStats(clean);
  return getMcTrafficProbeSnapshot(clean);
}
